import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { queryClient, type RouterInputs, trpc, trpcOptions, type trpcQueryUtils } from '@client/trpc/client';
import {
    CLAIM_RECORD_PREFETCH_OPTIONS,
    CLAIM_RECORD_QUERY_OPTIONS,
    DETAIL_RECORD_PREFETCH_OPTIONS,
} from '@client/trpc/query-options';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { ORG_ROLES } from '@shared/constants';
import { type QueryKey, skipToken } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { shouldPrefetchDetailRouteData } from '../../detail-route-prefetch';

/**
 * `claim.getClaim` input for the claim details page. The route's `beforeLoad` prefetch and
 * the ClaimDetails observer must serialise to the same query key, or the prefetch warms an
 * entry nothing reads — build it here instead of re-typing the literal.
 */
export function claimRecordInput(id: bigint): RouterInputs['claim']['getClaim'] {
    return { id };
}

/**
 * `claim.getFinancialSummary` input for the claim header's incurred cost. Shared by the route's
 * `beforeLoad` prefetch and the Header observer for the same reason as `claimRecordInput`.
 */
export function claimFinancialSummaryInput(id: bigint): RouterInputs['claim']['getFinancialSummary'] {
    return { claimId: id };
}

/**
 * Gate for the claim details routes' `beforeLoad` warm-ups (the details route and its tabs).
 * Mirrors the `/console/$orgId/claims` layout (roles and the claim module) and requires a
 * token outside the refresh window, so a signed-out deep link sends no anonymous request.
 */
export function isClaimDetailPrefetchAuthorised(routeOrgId: bigint): boolean {
    const authState = useAuthStore.getState();
    const user = selectUser(authState);
    return shouldPrefetchDetailRouteData({
        hasValidToken: authState.hasValidToken({
            minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
        }),
        userRoles: user?.roles,
        // The claims route's roles, the Accounts role included (CUR-2675).
        allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN, ORG_ROLES.ACCOUNTS],
        hydratedOrg: selectOrg(authState),
        routeOrgId,
        requiredModule: 'claim',
    });
}

/** The ClaimDetails observer of the claim record. */
export function useClaimRecordQuery(id: bigint | undefined) {
    return trpc.claim.getClaim.useQuery(id ? claimRecordInput(id) : skipToken, CLAIM_RECORD_QUERY_OPTIONS);
}

/**
 * Warm the claim record from the details route's `beforeLoad`, so `getClaim` starts while the
 * route chunks load instead of after ClaimDetails mounts.
 *
 * - Skipped while the record is already observed (ClaimDetails is mounted for this claim).
 *   `beforeLoad` reruns for the retained details match on every tab switch and on hover-intent
 *   preloads of in-page links; the mounted observer owns the record's freshness there, and a
 *   refetch would resend the full payload and re-render the details tree mid tab switch.
 *   Another claim, or a return to this one, is unobserved and still prefetches.
 * - A navigation uses the observer's freshness window, so a record the observer would refetch
 *   on mount is refreshed here and the observer joins the in-flight request.
 * - A hover-intent preload keeps the 30 s detail-record window: a cached record already paints
 *   instantly, and the click reruns `beforeLoad` as a navigation, which refreshes it.
 */
export function prefetchClaimRecord(
    trpcUtils: typeof trpcQueryUtils,
    id: bigint,
    { preload }: { preload: boolean },
): void {
    const input = claimRecordInput(id);
    if (isObserved(getQueryKey(trpc.claim.getClaim, input, 'query'))) {
        return;
    }
    void trpcUtils.claim.getClaim.prefetch(
        input,
        preload ? DETAIL_RECORD_PREFETCH_OPTIONS : CLAIM_RECORD_PREFETCH_OPTIONS,
    );
}

/**
 * Warm the header's incurred cost from the details route's `beforeLoad`, in the same tick as
 * `prefetchClaimRecord`, so `getFinancialSummary` joins the `getClaim` batch instead of leaving
 * in a later one once the Header has mounted from the record.
 *
 * - A hover-intent preload sends nothing: the click reruns `beforeLoad` as a navigation.
 * - Skipped while the summary is already observed (the Header is mounted for this claim), as
 *   `beforeLoad` reruns on every tab switch; financial writes invalidate the mounted observer.
 * - A navigation uses the Header observer's freshness window, so the observer joins the
 *   in-flight request instead of refetching on mount.
 */
export function prefetchClaimFinancialSummary(id: bigint, { preload }: { preload: boolean }): void {
    if (preload) {
        return;
    }
    const options = trpcOptions.claim.getFinancialSummary.queryOptions(
        claimFinancialSummaryInput(id),
        CLAIM_RECORD_PREFETCH_OPTIONS,
    );
    if (isObserved(options.queryKey)) {
        return;
    }
    void queryClient.prefetchQuery(options);
}

function isObserved(queryKey: QueryKey): boolean {
    return (queryClient.getQueryCache().find({ queryKey, exact: true })?.getObserversCount() ?? 0) > 0;
}
